import type { CurvaDocument } from "@/document/model/document";
import { frameCornerRadii, type FrameNode, type PrimitiveNode, type SceneNode, type VectorNetworkNode } from "@/document/model/nodes";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { normalizeFillStack, normalizeStrokeStack } from "@/document/model/styles";
import { createSceneTransformCache, type SceneTransformCache } from "@/document/operations/nodeWorldTransform";
import { primitiveToPolygon } from "@/document/operations/primitiveGeometry";
import { isClosedVectorNetwork, sampleEdgeBezier, sampleNetworkContours } from "@/document/operations/vectorGeometry";
import { adaptiveCurveSteps } from "@/document/operations/curveKernels";
import { filledVectorRegionContours } from "@/document/operations/vectorRegions";
import { pathDataToContours, pointFilledInContours, pointInContours } from "@/document/operations/pathGeometry";

export interface Point {
  x: number;
  y: number;
}

export function pointHitsNode(
  document: CurvaDocument,
  node: SceneNode,
  point: Point,
  tolerance: number,
  transformCache: SceneTransformCache = createSceneTransformCache(document),
): boolean {
  const localPoint = transformCache.pointToLocal(node.id, point);
  return pointHitsNodeLocal(node, localPoint, tolerance);
}

function pointHitsNodeLocal(node: SceneNode, localPoint: Point, tolerance: number): boolean {
  if (node.type === "group") {
    return false;
  }

  if (node.type === "frame") {
    const outline = frameOutlinePoints(node);
    return pointInPolygon(localPoint, outline) || pointNearPolyline(localPoint, outline, tolerance, true);
  }

  if (node.type === "primitive") {
    return pointHitsPrimitive(node, localPoint, tolerance);
  }

  if (node.type === "vectorNetwork") {
    return pointHitsVectorNetwork(node, localPoint, tolerance);
  }

  if (node.type === "text") {
    return rectContainsPoint(node.bounds, localPoint);
  }

  if (node.type === "shape") {
    const contours = pathDataToContours(node.path);
    return pointInContours(localPoint, contours) || contours.some(contour => pointNearPolyline(localPoint, contour, tolerance, true));
  }

  if (node.type === "boolean") {
    return pointInContours(localPoint, node.resolved.contours) || node.resolved.contours.some(contour => pointNearPolyline(localPoint, contour, tolerance, true));
  }

  return false;
}

export function nodeIntersectsMarquee(
  document: CurvaDocument,
  node: SceneNode,
  marquee: { x: number; y: number; width: number; height: number },
  transformCache: SceneTransformCache = createSceneTransformCache(document),
): boolean {
  const toWorld = (point: Point): Point => transformCache.pointToWorld(node.id, point);

  if (node.type === "group") {
    return false;
  }

  if (node.type === "frame") {
    const polygon = frameOutlinePoints(node).map(toWorld);
    return polygon.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(polygon, marquee);
  }

  if (node.type === "primitive") {
    if (node.primitive.kind === "oval") {
      const polygon = primitiveToPolygon(node).map(toWorld);
      return (
        rectContainsPoint(marquee, toWorld(ellipseCenter(node.bounds))) ||
        polygon.some(point => rectContainsPoint(marquee, point)) ||
        polygonEdgesIntersectRect(polygon, marquee)
      );
    }

    const polygon = primitiveToPolygon(node).map(toWorld);
    return polygon.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(polygon, marquee);
  }

  if (node.type === "vectorNetwork") {
    const points = sampleNetworkPoints(node.network).map(toWorld);
    return points.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(points, marquee);
  }

  if (node.type === "text") {
    const corners = rectPolygon(node.bounds).map(toWorld);
    return corners.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(corners, marquee);
  }

  if (node.type === "shape") {
    const contours = pathDataToContours(node.path);
    return contours.some(contour => {
      const worldContour = contour.map(toWorld);
      return worldContour.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(worldContour, marquee);
    });
  }

  if (node.type === "boolean") {
    return node.resolved.contours.some(contour => {
      const worldContour = contour.map(toWorld);
      return worldContour.some(point => rectContainsPoint(marquee, point)) || polygonEdgesIntersectRect(worldContour, marquee);
    });
  }

  return false;
}

export function nodeIntersectsLasso(
  document: CurvaDocument,
  node: SceneNode,
  lasso: Point[],
  transformCache: SceneTransformCache = createSceneTransformCache(document),
): boolean {
  if (lasso.length < 3 || node.type === "group") {
    return false;
  }

  const toWorld = (point: Point): Point => transformCache.pointToWorld(node.id, point);
  const shapePoints = sampledNodeOutlinePoints(node).map(toWorld);
  if (shapePoints.length === 0 || !rectsIntersect(boundsFromPoints(lasso), boundsFromPoints(shapePoints))) {
    return false;
  }

  return (
    shapePoints.some(point => pointInPolygon(point, lasso)) ||
    lasso.some(point => pointHitsNode(document, node, point, 0, transformCache)) ||
    polygonEdgesIntersectPolygon(shapePoints, lasso)
  );
}

function rectPolygon(rect: { x: number; y: number; width: number; height: number }): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

function frameOutlinePoints(frame: FrameNode): Point[] {
  const radii = normalizedFrameCornerRadii(frame);
  if (radii.every(radius => radius === 0)) {
    return rectPolygon(frame.bounds);
  }

  return roundedRectPoints(frame.bounds, radii, 8);
}

function normalizedFrameCornerRadii(frame: FrameNode): [number, number, number, number] {
  const maxRadius = Math.min(Math.abs(frame.bounds.width), Math.abs(frame.bounds.height)) / 2;
  return frameCornerRadii(frame).map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
}

function roundedRectPoints(
  rect: { x: number; y: number; width: number; height: number },
  radii: [number, number, number, number],
  stepsPerCorner: number,
): Point[] {
  const left = Math.min(rect.x, rect.x + rect.width);
  const right = Math.max(rect.x, rect.x + rect.width);
  const top = Math.min(rect.y, rect.y + rect.height);
  const bottom = Math.max(rect.y, rect.y + rect.height);
  const [topLeft, topRight, bottomRight, bottomLeft] = radii;
  const corners = [
    { center: { x: left + topLeft, y: top + topLeft }, radius: topLeft, start: Math.PI, end: Math.PI * 1.5 },
    { center: { x: right - topRight, y: top + topRight }, radius: topRight, start: Math.PI * 1.5, end: Math.PI * 2 },
    { center: { x: right - bottomRight, y: bottom - bottomRight }, radius: bottomRight, start: 0, end: Math.PI * 0.5 },
    { center: { x: left + bottomLeft, y: bottom - bottomLeft }, radius: bottomLeft, start: Math.PI * 0.5, end: Math.PI },
  ];
  const points: Point[] = [];

  for (const corner of corners) {
    if (corner.radius <= 0) {
      points.push({ x: corner.center.x, y: corner.center.y });
      continue;
    }
    for (let step = 0; step <= stepsPerCorner; step += 1) {
      const t = step / stepsPerCorner;
      const angle = corner.start + (corner.end - corner.start) * t;
      points.push({
        x: corner.center.x + Math.cos(angle) * corner.radius,
        y: corner.center.y + Math.sin(angle) * corner.radius,
      });
    }
  }

  return dedupePoints(points);
}

function dedupePoints(points: Point[]): Point[] {
  const deduped: Point[] = [];
  for (const point of points) {
    const previous = deduped.at(-1);
    if (!previous || previous.x !== point.x || previous.y !== point.y) {
      deduped.push(point);
    }
  }
  return deduped;
}

function pointHitsPrimitive(node: PrimitiveNode, point: Point, tolerance: number): boolean {
  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    if (!pointInEllipse(point, node.bounds)) return false;
    if (ratio > 0) {
      // Exclude the donut hole
      const cx = node.bounds.x + node.bounds.width / 2;
      const cy = node.bounds.y + node.bounds.height / 2;
      const rx = Math.abs(node.bounds.width) / 2 * ratio;
      const ry = Math.abs(node.bounds.height) / 2 * ratio;
      if (rx > 0 && ry > 0) {
        const nx = (point.x - cx) / rx;
        const ny = (point.y - cy) / ry;
        if (nx * nx + ny * ny <= 1) return false;
      }
    }
    if (arcSweep < 360) {
      // Check angle falls within the arc sector
      const cx = node.bounds.x + node.bounds.width / 2;
      const cy = node.bounds.y + node.bounds.height / 2;
      const angle = Math.atan2(point.y - cy, point.x - cx) * (180 / Math.PI) + 90;
      const normalised = ((angle % 360) + 360) % 360;
      const start = ((arcStart % 360) + 360) % 360;
      const rel = ((normalised - start) + 360) % 360;
      if (ratio === 0) {
        // Pie: check sector + center
        if (rel > arcSweep) return false;
      } else {
        if (rel > arcSweep) return false;
      }
    }
    return true;
  }

  if (node.primitive.kind === "line") {
    return distanceToSegment(point, { x: node.bounds.x, y: node.bounds.y }, {
      x: node.bounds.x + node.bounds.width,
      y: node.bounds.y + node.bounds.height,
    }) <= Math.max(tolerance, maxStrokeWidth(node) / 2);
  }

  return pointInPolygon(point, primitiveToPolygon(node));
}

function pointHitsVectorNetwork(node: VectorNetworkNode, point: Point, tolerance: number): boolean {
  const verticesById = vertexLookup(node.network.vertices);
  const edgeHit = node.network.edges.some(edge => {
    const points = sampleEdgePoints(verticesById, edge);
    return pointNearPolyline(point, points, Math.max(tolerance, maxStrokeWidth(node) / 2), false);
  });

  if (isClosedVectorNetwork(node.network)) {
    const hasFill = normalizeFillStack(node.style).length > 0;
    const contours = node.network.regions.length > 0
      ? filledVectorRegionContours(node.network)
      : sampleNetworkContours(node.network, edge => adaptiveStepsForEdge(verticesById, edge));
    return edgeHit || (hasFill && pointFilledInContours(point, contours, node.network.fillRule)) || contours.some(contour => pointNearPolyline(point, contour, tolerance, true));
  }

  return edgeHit;
}

function maxStrokeWidth(node: SceneNode): number {
  return Math.max(2, ...normalizeStrokeStack(node.style).map(stroke => stroke.width));
}

function pointInEllipse(point: Point, rect: { x: number; y: number; width: number; height: number }): boolean {
  const rx = Math.abs(rect.width) / 2;
  const ry = Math.abs(rect.height) / 2;
  if (rx <= 0 || ry <= 0) {
    return false;
  }

  const center = ellipseCenter(rect);
  const nx = (point.x - center.x) / rx;
  const ny = (point.y - center.y) / ry;
  return nx * nx + ny * ny <= 1;
}

function ellipseCenter(rect: { x: number; y: number; width: number; height: number }): Point {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const current = polygon[index]!;
    const previous = polygon[previousIndex]!;
    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y || Number.EPSILON) + current.x;
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

function pointNearPolyline(point: Point, points: Point[], tolerance: number, closed: boolean): boolean {
  const lastIndex = closed ? points.length : points.length - 1;
  for (let index = 0; index < lastIndex; index += 1) {
    const from = points[index]!;
    const to = points[(index + 1) % points.length]!;
    if (distanceToSegment(point, from, to) <= tolerance) {
      return true;
    }
  }

  return false;
}

function distanceToSegment(point: Point, from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= 0) {
    return Math.hypot(point.x - from.x, point.y - from.y);
  }

  const t = Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  const projection = { x: from.x + t * dx, y: from.y + t * dy };
  return Math.hypot(point.x - projection.x, point.y - projection.y);
}

function sampleNetworkPoints(network: VectorNetwork): Point[] {
  const verticesById = vertexLookup(network.vertices);
  const stepsFor = (edge: VectorEdge) => adaptiveStepsForEdge(verticesById, edge);
  if (isClosedVectorNetwork(network)) {
    return [...sampleNetworkContours(network, stepsFor).flat(), ...network.edges.flatMap(edge => sampleEdgePoints(verticesById, edge))];
  }

  return network.edges.flatMap(edge => sampleEdgePoints(verticesById, edge));
}

function vertexLookup(vertices: VectorVertex[]): Map<string, VectorVertex> {
  const map = new Map<string, VectorVertex>();
  for (const vertex of vertices) {
    map.set(vertex.id, vertex);
  }
  return map;
}

function adaptiveStepsForEdge(vertices: Map<string, VectorVertex>, edge: VectorEdge): number {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to) {
    return 1;
  }
  return adaptiveCurveSteps(from, to, edge.from.handle, edge.to.handle);
}

function sampleEdgePoints(vertices: Map<string, VectorVertex>, edge: VectorEdge): Point[] {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  return sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, adaptiveStepsForEdge(vertices, edge));
}

function rectContainsPoint(rect: { x: number; y: number; width: number; height: number }, point: Point): boolean {
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;
}

export function rectContainsRect(outer: { x: number; y: number; width: number; height: number }, inner: { x: number; y: number; width: number; height: number }): boolean {
  const left = Math.min(outer.x, outer.x + outer.width);
  const right = Math.max(outer.x, outer.x + outer.width);
  const top = Math.min(outer.y, outer.y + outer.height);
  const bottom = Math.max(outer.y, outer.y + outer.height);
  const innerLeft = Math.min(inner.x, inner.x + inner.width);
  const innerRight = Math.max(inner.x, inner.x + inner.width);
  const innerTop = Math.min(inner.y, inner.y + inner.height);
  const innerBottom = Math.max(inner.y, inner.y + inner.height);
  return innerLeft >= left && innerRight <= right && innerTop >= top && innerBottom <= bottom;
}

function rectsIntersect(left: { x: number; y: number; width: number; height: number }, right: { x: number; y: number; width: number; height: number }): boolean {
  return !(
    left.x + left.width < right.x ||
    right.x + right.width < left.x ||
    left.y + left.height < right.y ||
    right.y + right.height < left.y
  );
}

function polygonEdgesIntersectRect(points: Point[], rect: { x: number; y: number; width: number; height: number }): boolean {
  if (points.length < 2) {
    return false;
  }

  const rectPoints = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];

  for (let index = 0; index < points.length; index += 1) {
    const from = points[index]!;
    const to = points[(index + 1) % points.length]!;
    for (let rectIndex = 0; rectIndex < rectPoints.length; rectIndex += 1) {
      const rectFrom = rectPoints[rectIndex]!;
      const rectTo = rectPoints[(rectIndex + 1) % rectPoints.length]!;
      if (segmentsIntersect(from, to, rectFrom, rectTo)) {
        return true;
      }
    }
  }

  return false;
}

function polygonEdgesIntersectPolygon(first: Point[], second: Point[]): boolean {
  if (first.length < 2 || second.length < 2) {
    return false;
  }

  for (let firstIndex = 0; firstIndex < first.length; firstIndex += 1) {
    const firstFrom = first[firstIndex]!;
    const firstTo = first[(firstIndex + 1) % first.length]!;
    for (let secondIndex = 0; secondIndex < second.length; secondIndex += 1) {
      const secondFrom = second[secondIndex]!;
      const secondTo = second[(secondIndex + 1) % second.length]!;
      if (segmentsIntersect(firstFrom, firstTo, secondFrom, secondTo)) {
        return true;
      }
    }
  }

  return false;
}

function sampledNodeOutlinePoints(node: SceneNode): Point[] {
  if (node.type === "frame") {
    return frameOutlinePoints(node);
  }

  if (node.type === "text") {
    return rectPolygon(node.bounds);
  }

  if (node.type === "primitive") {
    return primitiveToPolygon(node);
  }

  if (node.type === "vectorNetwork") {
    return sampleNetworkPoints(node.network);
  }

  if (node.type === "shape") {
    return pathDataToContours(node.path).flat();
  }

  if (node.type === "boolean") {
    return node.resolved.contours.flat();
  }

  return [];
}

function boundsFromPoints(points: Point[]): { x: number; y: number; width: number; height: number } {
  const minX = Math.min(...points.map(point => point.x));
  const minY = Math.min(...points.map(point => point.y));
  const maxX = Math.max(...points.map(point => point.x));
  const maxY = Math.max(...points.map(point => point.y));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = direction(c, d, a);
  const d2 = direction(c, d, b);
  const d3 = direction(a, b, c);
  const d4 = direction(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function direction(a: Point, b: Point, c: Point): number {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}
